import * as React from "react";
import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import type { Session, User, AuthenticatorAssuranceLevels } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { isDeviceTrusted } from "@/utils/trustedDevice";

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T | null> => {
  let timeoutId: number | undefined;
  const timeoutPromise = new Promise<null>((resolve) => {
    timeoutId = window.setTimeout(() => {
      console.warn(`[Auth] ${label} timed out after ${timeoutMs}ms`);
      resolve(null);
    }, timeoutMs);
  });

  try {
    return (await Promise.race([promise, timeoutPromise])) as T | null;
  } finally {
    if (timeoutId) window.clearTimeout(timeoutId);
  }
};

const getSessionAal = (session: Session | null): AuthenticatorAssuranceLevels | null => {
  try {
    const payload = session?.access_token?.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded?.aal ?? null;
  } catch {
    return null;
  }
};

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  needsPasswordReauth: boolean;
  setNeedsPasswordReauth: (value: boolean) => void;
  needsMFAVerification: boolean;
  setNeedsMFAVerification: (value: boolean) => void;
  currentAssuranceLevel: AuthenticatorAssuranceLevels | null;
  signInWithEmail: (email: string, password: string) => Promise<{ error: Error | null; needsMFA?: boolean }>;
  signUpWithEmail: (email: string, password: string, metadata?: { first_name?: string; last_name?: string; company?: string; firm_name?: string; phone?: string; state?: string }, redirectUrl?: string, userType?: string) => Promise<{ error: Error | null }>;
  signInWithLinkedIn: () => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPasswordForEmail: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
  updateEmail: (email: string) => Promise<{ error: Error | null }>;
  createProfile: (uid: string, userData?: { first_name?: string; last_name?: string; company?: string; email?: string; state?: string }) => Promise<void>;
  completeMFAVerification: () => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsPasswordReauth, setNeedsPasswordReauth] = useState(false);
  const [needsMFAVerification, setNeedsMFAVerification] = useState(false);
  const [currentAssuranceLevel, setCurrentAssuranceLevel] = useState<AuthenticatorAssuranceLevels | null>(null);
  const initialLoadWatchdogRef = useRef<number | null>(null);
  const mfaPendingSessionRef = useRef<Session | null>(null);
  const initialSessionCheckCompleteRef = useRef(false);
  const lastEstablishedSessionKeyRef = useRef<string | null>(null);

  const createProfile = async (uid: string, userData?: { first_name?: string; last_name?: string; company?: string; email?: string; state?: string }) => {
    try {
      console.log('Creating/updating profile for user:', uid);
      
      // Simple upsert without any complex operations that might trigger net schema errors
      const profileData = {
        id: uid,
        email: userData?.email || null,
        first_name: userData?.first_name || null,
        last_name: userData?.last_name || null,
        company: userData?.company || null,
        state: userData?.state || null,
        updated_at: new Date().toISOString(),
      };

      const { error: upsertError } = await supabase
        .from('profiles')
        .upsert(profileData, { 
          onConflict: 'id',
          ignoreDuplicates: false 
        });

      if (upsertError) {
        console.error('Error creating/updating profile:', upsertError);
        // Don't throw on certain types of errors that might be related to triggers
        if (upsertError.code === '3F000') {
          console.warn('Schema error ignored during profile creation - this is likely a trigger issue');
          return; // Continue without throwing
        }
        throw new Error(`Failed to save user profile: ${upsertError.message}`);
      } else {
        console.log('Profile created/updated successfully for user:', uid);
      }
    } catch (error: any) {
      console.error('Unexpected error in createProfile:', error);
      // Don't throw schema errors - they're likely from triggers we can't control
      if (error.message && error.message.includes('schema "net" does not exist')) {
        console.warn('Net schema error ignored - continuing with signup');
        return;
      }
      throw error;
    }
  };

  // Enhanced function to ensure profile exists for any authentication
  const ensureProfile = async (user: User) => {
    try {
      // Check if profile already exists
      const { data: existingProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (fetchError) {
        console.error('Error checking existing profile:', fetchError);
        return;
      }

      // If profile doesn't exist, create one with available user metadata
      if (!existingProfile) {
        console.log('Profile not found, creating one for user:', user.id);
        const userData = {
          first_name: user.user_metadata?.first_name || user.user_metadata?.name?.split(' ')[0] || null,
          last_name: user.user_metadata?.last_name || user.user_metadata?.name?.split(' ').slice(1).join(' ') || null,
          company: user.user_metadata?.company || null,
          email: user.email || null
        };
        
        await createProfile(user.id, userData);
      } else {
        console.log('Profile already exists for user:', user.id);
      }
    } catch (error) {
      console.error('Error ensuring profile exists:', error);
      // Don't throw - profile creation should not block authentication
    }
  };

  useEffect(() => {
    // Failsafe: never allow the app to stay on the global auth "Loading..." screen forever
    initialLoadWatchdogRef.current = window.setTimeout(() => {
      console.warn('[Auth] Initial auth load watchdog fired — forcing loading=false');
      setLoading(false);
    }, 8000);

    const clearWatchdog = () => {
      if (initialLoadWatchdogRef.current) {
        window.clearTimeout(initialLoadWatchdogRef.current);
        initialLoadWatchdogRef.current = null;
      }
    };

    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      console.log('Auth state change:', event, s?.user?.id);

      if (event === 'INITIAL_SESSION' && !s && !initialSessionCheckCompleteRef.current) {
        console.log('[Auth] Waiting for explicit session restore before treating INITIAL_SESSION as signed out');
        return;
      }

      const establishSession = (nextSession: Session | null) => {
        const sessionKey = nextSession ? `${nextSession.user.id}:${nextSession.access_token}` : 'signed-out';
        if (lastEstablishedSessionKeyRef.current === sessionKey) {
          clearWatchdog();
          setLoading(false);
          return;
        }
        lastEstablishedSessionKeyRef.current = sessionKey;

        setSession(nextSession);
        setUser(nextSession?.user ?? null);
        setCurrentAssuranceLevel(getSessionAal(nextSession));

        if (nextSession?.user) {
          setTimeout(() => {
            ensureProfile(nextSession.user);
          }, 0);
        }

        clearWatchdog();
        setLoading(false);
      };

      // Supabase auth callbacks are lock-sensitive. Defer follow-up auth calls
      // so MFA checks do not deadlock/time out and cause post-2FA route loops.
      if (s && event === 'SIGNED_IN') {
        window.setTimeout(async () => {
          try {
            const aalRes = await withTimeout(
              supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
              7000,
              'mfa.getAuthenticatorAssuranceLevel (onAuthStateChange)'
            );
            const aalData = aalRes?.data;

            if (aalData && aalData.nextLevel === 'aal2' && aalData.currentLevel === 'aal1') {
              if (s.user && isDeviceTrusted(s.user.id)) {
                console.log('🔒 Device is trusted, skipping MFA verification');
              } else {
                console.log('🔐 onAuthStateChange: MFA pending, not setting session yet');
                mfaPendingSessionRef.current = s;
                setNeedsMFAVerification(true);
                clearWatchdog();
                setLoading(false);
                return;
              }
            }
          } catch (mfaError) {
            console.error('Error checking MFA status in auth state change:', mfaError);
          }

          establishSession(s);
        }, 0);
        return;
      }

      if (event === 'MFA_CHALLENGE_VERIFIED') {
        window.setTimeout(async () => {
          const { data } = await supabase.auth.getSession();
          mfaPendingSessionRef.current = null;
          setNeedsMFAVerification(false);
          establishSession(data.session);
        }, 0);
        return;
      }

      establishSession(s);
    });

    // THEN check for existing session with retry logic for better reliability
    const checkSession = async () => {
      console.log('Checking initial session...');
      let session = null;
      let sessionError = null;

      // Retry session check up to 3 times for transient network/DB failures
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const result = await supabase.auth.getSession();
          session = result.data?.session;
          sessionError = result.error;
          if (!sessionError) break;
        } catch (e) {
          sessionError = e;
        }

        if (attempt < 2) {
          const delay = 1000 * Math.pow(2, attempt) + Math.random() * 500;
          console.warn(`[Auth] Session check attempt ${attempt + 1} failed. Retrying in ${Math.round(delay)}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      if (sessionError) {
        console.error('Session check error after retries:', sessionError);
      }

      initialSessionCheckCompleteRef.current = true;

      console.log('Initial session check:', session?.user?.id);

      // CRITICAL: Check if MFA verification is pending before setting session state
      if (session) {
        try {
          const aalRes = await withTimeout(
            supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
            2500,
            'mfa.getAuthenticatorAssuranceLevel (initialSession)'
          );
          const aalData = aalRes?.data;

          if (aalData && aalData.nextLevel === 'aal2' && aalData.currentLevel === 'aal1') {
            if (session?.user && isDeviceTrusted(session.user.id)) {
              console.log('🔒 Device is trusted, skipping MFA on initial session');
            } else {
              console.log('🔐 Initial session check: MFA pending, not setting session yet');
              mfaPendingSessionRef.current = session;
              setNeedsMFAVerification(true);
              clearWatchdog();
              setLoading(false);
              return;
            }
          }
        } catch (mfaError) {
          console.error('Error checking MFA status in initial session:', mfaError);
        }
      }

      const sessionKey = session ? `${session.user.id}:${session.access_token}` : 'signed-out';
      const isDuplicateSession = lastEstablishedSessionKeyRef.current === sessionKey;
      if (!isDuplicateSession) {
        lastEstablishedSessionKeyRef.current = sessionKey;
        setSession(session);
        setUser(session?.user ?? null);
        setCurrentAssuranceLevel(getSessionAal(session));
      }

      // Ensure profile exists for existing session
      if (session?.user && !isDuplicateSession) {
        setTimeout(() => {
          ensureProfile(session.user);
        }, 0);

        // TEMPORARILY DISABLED: Encryption modal check
        // This is disabled during signup flow troubleshooting
        // Will be re-enabled once signup flow is confirmed working
        /*
        const hasEncryptionKeys = sessionStorage.getItem(`enc_key_${session.user.id}`) !== null;

        const authTimestamp = localStorage.getItem('last_auth_timestamp');
        const isRecentLogin = authTimestamp && (Date.now() - parseInt(authTimestamp)) < 5000;

        if (!hasEncryptionKeys && !isRecentLogin) {
          console.log('⚠️ Session restored without encryption keys - prompting for password');
          setNeedsPasswordReauth(true);
        }
        */
      }

      clearWatchdog();
      setLoading(false);
    };

    checkSession();

    return () => {
      clearWatchdog();
      subscription.unsubscribe();
    };
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    // Retry logic for transient Supabase DB connection failures (504, timeout, ECONNRESET)
    const MAX_RETRIES = 3;
    let lastError: any = null;
    let data: any = null;
    let error: any = null;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      const result = await supabase.auth.signInWithPassword({ email, password });
      data = result.data;
      error = result.error;

      // If success or non-retryable error, break immediately
      if (!error) break;

      const msg = (error?.message || '').toLowerCase();
      const isRetryable = msg.includes('timeout') || msg.includes('504') || 
        msg.includes('500') || msg.includes('database') || msg.includes('connection') ||
        msg.includes('fetch failed') || msg.includes('network') || msg.includes('econnreset');
      
      if (!isRetryable || attempt === MAX_RETRIES) break;

      lastError = error;
      const delay = Math.min(1000 * Math.pow(2, attempt) + Math.random() * 500, 8000);
      console.warn(`[Auth] Sign-in attempt ${attempt + 1} failed (${error.message}). Retrying in ${Math.round(delay)}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    // If signin successful, check for MFA requirements
    if (!error && data.session) {
      // Check if MFA verification is required BEFORE setting session state
      const aalRes = await withTimeout(
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
        2500,
        'mfa.getAuthenticatorAssuranceLevel (signInWithEmail)'
      );

      const aalData = aalRes?.data;
      const aalError = aalRes?.error;

      if (!aalError && aalData) {
        setCurrentAssuranceLevel(aalData.currentLevel);
        console.log('MFA Status:', {
          currentLevel: aalData.currentLevel,
          nextLevel: aalData.nextLevel,
          currentAuthMethods: aalData.currentAuthenticationMethods,
        });

        // If next level is higher than current level, MFA verification is needed
        // CRITICAL: Do NOT set session state until MFA is verified to prevent bypass
        if (aalData.nextLevel === 'aal2' && aalData.currentLevel === 'aal1') {
          if (data.user && isDeviceTrusted(data.user.id)) {
            console.log('🔒 Device is trusted, skipping MFA on sign-in');
          } else {
            console.log('🔐 MFA verification required - user has 2FA enabled, blocking login until verified');
              mfaPendingSessionRef.current = data.session;
            setNeedsMFAVerification(true);
            return { error: null, needsMFA: true };
          }
        }
      }

      // Only set session state if MFA is not required or already satisfied
      setSession(data.session);
      setUser(data.user);
      console.log('Sign in successful - session established:', data.session.access_token.substring(0, 20) + '...');
      
      // Mark this as a recent login
      localStorage.setItem('last_auth_timestamp', Date.now().toString());
    }
    
    return { error: error as Error | null, needsMFA: false };
  };

  const completeMFAVerification = async () => {
    // After mfa.verify(), Supabase has stored an upgraded aal2 session.
    // Always prefer the freshly stored session over the cached pre-MFA (aal1) one,
    // otherwise subsequent reloads/token refreshes will re-detect nextLevel=aal2
    // and prompt for MFA again.
    const { data: refreshed } = await supabase.auth.getSession();
    const nextSession = refreshed.session ?? mfaPendingSessionRef.current;
    if (!nextSession?.user) {
      return { error: new Error('MFA verification succeeded, but the pending session was not available. Please sign in again.') };
    }

    mfaPendingSessionRef.current = null;
    setNeedsMFAVerification(false);
    setSession(nextSession);
    setUser(nextSession.user);
    localStorage.setItem('last_auth_timestamp', Date.now().toString());
    setTimeout(() => {
      ensureProfile(nextSession.user);
    }, 0);

    return { error: null };
  };

  const signUpWithEmail = async (email: string, password: string, metadata?: { first_name?: string; last_name?: string; company?: string; firm_name?: string; phone?: string; state?: string }, customRedirectUrl?: string, userType?: string) => {
    console.log('🎯 AuthContext.signUpWithEmail called with:', {
      email,
      hasMetadata: !!metadata,
      metadata,
      customRedirectUrl,
      userType,
      userTypeType: typeof userType
    });
    
    if (!userType) {
      console.warn('⚠️ WARNING: userType is undefined or null!');
    }
    
    if (userType !== 'attorney' && userType !== 'client') {
      console.error('❌ INVALID userType:', userType, '- should be "attorney" or "client"');
    }
    
    const redirectUrl = customRedirectUrl || `${window.location.origin}/auth/confirm`;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { 
        emailRedirectTo: redirectUrl,
        data: {
          signup_type: userType, // Track attorney vs client signups
          ...(metadata || {}) // Spread metadata to include all profile fields
        }
      },
    });
    
    console.log('🎯 SIGNUP COMPLETED:', {
      hasError: !!error,
      errorMessage: error?.message,
      hasUser: !!data.user,
      hasSession: !!data.session,
      userId: data.user?.id,
      userType: userType,
      signupType: data.user?.user_metadata?.signup_type,
      metadata: metadata
    });
    
    console.log('🔍 Signup result analysis:', {
      hasError: !!error,
      hasUser: !!data.user,
      hasSession: !!data.session,
      userId: data.user?.id,
      signupType: data.user?.user_metadata?.signup_type,
      userTypeParam: userType
    });

    // CRITICAL: Handle attorney setup for BOTH immediate signin and email confirmation cases
    // Attorney account setup should happen regardless of whether email confirmation is required
    if (!error && data.user) {
      const isAttorneySignup = userType === 'attorney' || data.user.user_metadata?.signup_type === 'attorney';
      
      console.log('🎯 Attorney signup detection:', {
        isAttorney: isAttorneySignup,
        userTypeParam: userType,
        metadataSignupType: data.user.user_metadata?.signup_type,
        hasSession: !!data.session
      });

      // If email confirmations are disabled, the user will be signed in immediately
      if (data.session) {
        console.log('✅ User signed in immediately (confirmations disabled)');
        setSession(data.session);
        setUser(data.user);
        console.log('Sign up successful - session established:', data.session.access_token.substring(0, 20) + '...');
        
        // Mark this as a recent signup to prevent password prompt
        localStorage.setItem('last_auth_timestamp', Date.now().toString());
        
        // Migrate anonymous session data if available
        try {
          const anonymousSessionId = localStorage.getItem('anon_session_id');
          if (anonymousSessionId) {
            console.log('🔄 Migrating anonymous session data for new user:', data.user.id);
            
            // Migrate both chat sessions and usage data in parallel
            const [sessionResult, usageResult] = await Promise.allSettled([
              supabase.functions.invoke('migrate-anonymous-session', {
                body: { anonymousSessionId }
              }),
              supabase.functions.invoke('migrate-anonymous-usage', {
                body: { anonymousSessionId }
              })
            ]);
            
            const sessionSuccess = sessionResult.status === 'fulfilled' && !sessionResult.value.error;
            const usageSuccess = usageResult.status === 'fulfilled' && !usageResult.value.error;
            
            if (!sessionSuccess) {
              console.error('❌ Session migration error:', 
                sessionResult.status === 'rejected' ? sessionResult.reason : sessionResult.value.error);
            } else {
              console.log('✅ Chat sessions migrated:', sessionResult.value.data);
            }
            
            if (!usageSuccess) {
              console.error('❌ Usage migration error:', 
                usageResult.status === 'rejected' ? usageResult.reason : usageResult.value.error);
            } else {
              console.log('✅ Usage data migrated:', usageResult.value.data);
            }
            
            if (sessionSuccess || usageSuccess) {
              // Clear the anonymous session ID after successful migration
              localStorage.removeItem('anon_session_id');
              console.log('✅ Anonymous session data migrated - history preserved');
            }
          }
        } catch (migrationError) {
          console.error('Error during anonymous session migration (non-critical):', migrationError);
          // Don't fail signup for migration errors
        }
      }

      // CRITICAL: Set up attorney account REGARDLESS of session status
      // This ensures attorney accounts are properly initialized even with email confirmation enabled
      if (isAttorneySignup) {
        console.log('🎯 ATTORNEY SETUP BLOCK ENTERED');
        console.log('📋 Attorney setup data:', {
          userId: data.user.id,
          email: data.user.email,
          metadata: metadata,
          userType: userType
        });
        
        const maxRetries = 2;
        let lastError: Error | null = null;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            console.log(`🔄 Setting up attorney account (attempt ${attempt}/${maxRetries}) for user:`, data.user.id);
            
            const { data: setupData, error: attorneySetupError } = await supabase.functions.invoke('setup-attorney-account', {
              body: { 
                user_id: data.user.id,
                user_metadata: metadata
              }
            });
            
            if (attorneySetupError) {
              throw new Error(attorneySetupError.message);
            }
            
            // Verify the response indicates success
            if (!setupData?.success) {
              throw new Error(setupData?.error || 'Attorney setup did not complete successfully');
            }
            
            console.log('✅ Attorney account setup completed successfully');
            lastError = null;
            break; // Success - exit retry loop
            
          } catch (setupError) {
            lastError = setupError instanceof Error ? setupError : new Error('Unknown error during attorney setup');
            console.error(`❌ Attorney setup attempt ${attempt} failed:`, lastError.message);
            
            // If this was the last attempt, throw the error to block signup
            if (attempt === maxRetries) {
              throw new Error(
                `Failed to set up attorney account after ${maxRetries} attempts: ${lastError.message}. Please try signing up again or contact support.`
              );
            }
            
            // Wait before retry (exponential backoff: 1s, 2s)
            await new Promise(resolve => setTimeout(resolve, attempt * 1000));
          }
        }
      } else {
        console.log('🎯 CLIENT SETUP BLOCK ENTERED (NOT ATTORNEY)');
        console.log('📋 Received userType:', userType, 'Expected: "attorney" but got:', userType);
        
        // For client signups, ensure they have the basic 'user' role
        try {
          console.log('Setting up client role for user:', data.user.id);
          const { error: roleError } = await supabase
            .from('user_roles')
            .insert({
              user_id: data.user.id,
              role: 'user'
            });
          
          if (roleError) {
            console.error('Client role assignment error:', roleError);
            // Don't fail signup for role assignment errors
          } else {
            console.log('Client role assigned successfully');
            
            // Notify admins of new client signup (non-blocking)
            try {
              console.log('📧 Notifying admins of new client signup...');
              await supabase.functions.invoke('notify-admin-new-user', {
                body: {
                  user_name: `${metadata?.first_name || 'New'} ${metadata?.last_name || 'User'}`,
                  user_email: email,
                  user_type: 'client',
                  company: metadata?.company || null,
                  signup_date: new Date().toISOString()
                }
              });
              console.log('✅ Admin notification sent for client signup');
            } catch (notifyError) {
              console.error('Admin notification error (non-blocking):', notifyError);
            }
          }
        } catch (roleSetupError) {
          console.error('Error setting up client role:', roleSetupError);
          // Don't fail the entire signup
        }
      }
    }
    
    return { error: error as Error | null };
  };

  const signInWithLinkedIn = async () => {
    const redirectTo = `${window.location.origin}/auth?redirect=true`;
    console.log('[OAuth] signInWithLinkedIn redirectTo:', redirectTo);
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'linkedin_oidc',
      options: { redirectTo },
    });
    if (error) {
      console.error('[OAuth] LinkedIn sign-in error:', error);
    } else {
      console.log('[OAuth] LinkedIn provider URL:', data?.url);
    }
    return { error: error as Error | null };
  };

  const signInWithGoogle = async () => {
    const redirectTo = `${window.location.origin}/auth?redirect=true`;
    console.log('[OAuth] signInWithGoogle redirectTo:', redirectTo);
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
    if (error) {
      console.error('[OAuth] Google sign-in error:', error);
    } else {
      console.log('[OAuth] Google provider URL:', data?.url);
    }
    return { error: error as Error | null };
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.log('Sign out error (ignoring):', error);
    }
    // Force clear local session state and localStorage
    setSession(null);
    setUser(null);
    // CRITICAL: Clear MFA state to prevent stale 2FA screens on next login
    setNeedsMFAVerification(false);
    setCurrentAssuranceLevel(null);
    mfaPendingSessionRef.current = null;
    // Clear localStorage explicitly to prevent auto-signin
    localStorage.removeItem('sb-quidbuzjosjewriszapj-auth-token');
    // Let the auth state change listener handle the redirect
  };

  const resetPasswordForEmail = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth?reset=true`,
    });
    return { error: error as Error | null };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: error as Error | null };
  };

  const updateEmail = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth/confirm?type=email_change`;
    const { error } = await supabase.auth.updateUser(
      { email },
      { emailRedirectTo: redirectUrl }
    );
    return { error: error as Error | null };
  };

  const value = useMemo(() => ({
    user,
    session,
    loading,
    needsPasswordReauth,
    setNeedsPasswordReauth,
    needsMFAVerification,
    setNeedsMFAVerification,
    currentAssuranceLevel,
    signInWithEmail,
    signUpWithEmail,
    signInWithLinkedIn,
    signInWithGoogle,
    signOut,
    resetPasswordForEmail,
    updatePassword,
    updateEmail,
    createProfile,
    completeMFAVerification,
  }), [user, session, loading, needsPasswordReauth, needsMFAVerification, currentAssuranceLevel]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useOptionalAuth = () => {
  return useContext(AuthContext);
};

export const useAuth = () => {
  const ctx = useOptionalAuth();
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

